/**
 * "Set up this bank" (B6) -- the admin console opens with the order.
 *
 * It opened with 32 unordered tiles in three groups, Bank Setup twelfth
 * under Ceremony, and two contradicting ladders neither of which was
 * linked from here. An administrator arriving for the first time had no
 * way to know what to do first.
 *
 * Read from the journey spine, filtered to the steps an administrator
 * or a data steward owns, in stage order -- so this is not a third
 * ladder, it is the same one seen from the admin's end. It ends by
 * naming the hand-over, because the plumbing is not the job: it is what
 * has to be true before the Resilience Lead can start hers.
 */
import { Fragment } from "react";
import { Arrow } from "shared/components/Arrow";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useJourneySpineStatus } from "shared/journey/useJourney";
import { STEP_META } from "shared/journey/stepMeta";
import type { JourneySpineStep } from "shared/api/types";

const TONE: Record<string, string> = {
  done: "bg-emerald-100 text-emerald-900",
  partial: "bg-amber-100 text-amber-900",
  blocked: "bg-rose-100 text-rose-900",
  todo: "bg-slate-100 text-slate-600",
};

function Row({ step }: { step: JourneySpineStep }) {
  const { t } = useTranslation();
  const meta = STEP_META[step.key];
  return (
    <li
      data-testid={`setup-step-${step.key}`}
      className="flex items-center gap-3 border-b border-slate-100 py-1.5 last:border-0"
    >
      <span
        className={`w-16 shrink-0 rounded px-1.5 py-0.5 text-center text-[10px] font-semibold uppercase ${
          TONE[step.status] ?? TONE.todo
        }`}
      >
        {t(`modules.journey.status.${step.status}`)}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm text-slate-800">
        {t(`modules.journey.step.${step.key}.title`)}
      </span>
      {/* Who does it -- the question an administrator asks second. */}
      <span className="hidden w-24 shrink-0 text-xs text-slate-500 sm:block">
        {meta ? t(`modules.journey.owner.${meta.owner}`) : ""}
      </span>
      {step.total !== null && (
        <span className="w-14 shrink-0 text-end text-xs tabular-nums text-slate-400">
          {step.count}/{step.total}
        </span>
      )}
      {meta && step.status !== "done" && (
        <Link
          to={meta.route}
          data-testid={`setup-go-${step.key}`}
          className="shrink-0 text-xs text-brand-brassDark hover:underline"
        >
          {t("modules.journey.go")}
        </Link>
      )}
    </li>
  );
}

/**
 * Round 12 A7 -- the rung that needs a shell.
 *
 * Bank Setup delivers the day's most honest sentence and the first
 * genuine wall, with the exact command to generate a key. Nothing on
 * any of the four ladders said that one of their rungs could not be
 * closed from the screens, and that was the rung still open at five
 * o'clock.
 */
export function DeploymentWall({ step }: { step: JourneySpineStep }) {
  const { t } = useTranslation();
  const name = step.needs_deployment_setting;
  if (!name) return null;
  return (
    <li
      data-testid={`setup-wall-${step.key}`}
      className="flex flex-wrap items-baseline gap-2 border-b border-slate-100 py-1.5 ps-[4.75rem] text-xs text-amber-900 last:border-0"
    >
      <span>{t("adminSetup.needsDeploymentSetting", { name })}</span>
      <Link to="/admin/bank-setup" className="text-brand-brassDark hover:underline">
        {t("adminSetup.needsDeploymentSettingCta")} <Arrow />
      </Link>
    </li>
  );
}

export function SetUpThisBank() {
  const { t } = useTranslation();
  const journey = useJourneySpineStatus();
  const steps = (journey.data?.steps ?? []).filter((s) => {
    const owner = STEP_META[s.key]?.owner;
    return owner === "admin" || owner === "steward";
  });
  if (steps.length === 0) return null;

  const outstanding = steps.filter((s) => s.status !== "done").length;

  return (
    <section
      data-testid="admin-setup-strip"
      className="rounded border border-slate-200 bg-white p-4"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h2 className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
            {t("adminSetup.heading")}
          </h2>
          {/* A7 -- three of the four ladders said only a number, and two
              of them said "0 of 7" about different sevens. Name the set
              being counted. */}
          <p data-testid="admin-setup-whose" className="text-xs text-slate-500">
            {t("adminSetup.whose", {
              done: steps.length - outstanding,
              total: steps.length,
            })}
          </p>
        </div>
        <Link
          to="/admin/setup"
          data-testid="admin-setup-journey-link"
          className="text-xs text-brand-brassDark hover:underline"
        >
          {t("adminSetup.wholeJourney")}
        </Link>
      </div>
      <ul className="mt-2">
        {steps.map((s) => (
          <Fragment key={s.key}>
            <Row step={s} />
            <DeploymentWall step={s} />
          </Fragment>
        ))}
      </ul>
      <p data-testid="admin-setup-handover" className="mt-3 text-xs text-slate-600">
        {outstanding === 0
          ? t("adminSetup.handoverReady")
          : t("adminSetup.handover", { count: outstanding })}
      </p>
    </section>
  );
}
